import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock, RefreshCw, Mail, Phone, FileText, ChevronDown, ChevronUp, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ff_admin_pw";

// Field group definitions for showing the intake in human-readable sections.
const INTAKE_GROUPS = [
  { title: "Personal", keys: [
    ["Full name", "client_full_name"], ["Middle name on ID", "middle_name_on_id"],
    ["DOB", "date_of_birth"], ["SSN", "ssn"], ["Gender", "gender"],
    ["DL #", "drivers_license_number"], ["DL expires", "drivers_license_expiration"],
    ["Height", "height"], ["Weight", "weight"],
    ["Home address", "home_address"], ["Years at address", "years_at_address"],
    ["Phone", "phone"], ["Email", "email"],
    ["State/Country of birth", "state_country_of_birth"],
    ["Marital status", "marital_status"], ["US citizen", "us_citizen"],
  ]},
  { title: "Employment", keys: [
    ["Employer", "employer_name_type"], ["Tenure", "employment_length"],
    ["Employer address", "employer_address"], ["Position", "position"],
    ["Annual income", "annual_income"], ["Net worth", "net_worth"],
  ]},
  { title: "Doctor", keys: [
    ["Doctor", "doctor_name"], ["Phone", "doctor_phone"], ["Address", "doctor_address"],
    ["Last visit", "last_visit_date"], ["Visit reason / results", "last_visit_reason_results"],
  ]},
  { title: "Legal", keys: [["Felonies/DUI", "felonies_misdemeanors_dui"]] },
  { title: "Primary beneficiary", keys: [
    ["Name & %", "primary_beneficiary_name_pct"], ["SSN", "primary_beneficiary_ssn"],
    ["Relationship", "primary_beneficiary_relationship"], ["DOB", "primary_beneficiary_dob"],
  ]},
  { title: "Second primary beneficiary", keys: [
    ["Name & %", "second_beneficiary_name_pct"], ["DOB", "second_beneficiary_dob"],
    ["Relationship", "second_beneficiary_relationship"],
  ]},
  { title: "Contingent", keys: [
    ["Contingent #1", "contingent_beneficiary_1"], ["Contingent #2", "contingent_beneficiary_2"],
  ]},
  { title: "Banking", keys: [
    ["Bank", "bank_name"], ["Name on account", "bank_account_name"],
    ["Account #", "bank_account_number"], ["Routing #", "bank_routing_number"],
  ]},
  { title: "Health & lifestyle", keys: [
    ["Tobacco history", "tobacco_history"], ["Siblings", "siblings"],
    ["Parents", "parents_status"], ["Medical conditions", "medical_conditions_medications"],
    ["Regular meds", "regular_medications"], ["Alcohol", "alcohol_use"],
  ]},
  { title: "Travel", keys: [["Travel next 12 mo", "travel_next_12_months"]] },
  { title: "Medical exam", keys: [["Availability", "medical_exam_availability"]] },
  { title: "Agent", keys: [["Referring agent", "agent_name"]] },
];

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async (pw) => {
    setLoading(true);
    let authFailed = false;
    const leadsResult = await axios
      .get(`${API}/admin/leads`, { headers: { "X-Admin-Password": pw } })
      .then((res) => ({ ok: true, data: res.data || [] }))
      .catch((err) => {
        if (err?.response?.status === 401) authFailed = true;
        return { ok: false, data: [] };
      });
    const intakesResult = await axios
      .get(`${API}/admin/intakes`, { headers: { "X-Admin-Password": pw } })
      .then((res) => ({ ok: true, data: res.data?.intakes || [] }))
      .catch((err) => {
        if (err?.response?.status === 401) authFailed = true;
        return { ok: false, data: [] };
      });

    if (authFailed) {
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
      toast.error("Session expired. Please sign in again.");
      setLoading(false);
      return;
    }

    setLeads(leadsResult.data);
    setIntakes(intakesResult.data);
    if (!leadsResult.ok && !intakesResult.ok) {
      toast.error("Failed to load data.");
    }
    setLoading(false);
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPassword(stored);
      setAuthed(true);
      loadAll(stored);
    }
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
      toast.success("Welcome back.");
      loadAll(password);
    } catch (err) {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
    setLeads([]);
    setIntakes([]);
  };

  const toggleContacted = async (leadId, contacted) => {
    // Optimistic update
    setLeads((prev) =>
      prev.map((l) =>
        l.id === leadId
          ? { ...l, contacted, contacted_at: contacted ? new Date().toISOString() : null }
          : l
      )
    );
    try {
      await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { contacted },
        { headers: { "X-Admin-Password": password } }
      );
    } catch (err) {
      toast.error("Could not update lead. Reverting.");
      // Revert
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId
            ? { ...l, contacted: !contacted }
            : l
        )
      );
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={onLogin}
          className="w-full max-w-md rounded-3xl bg-card border border-border/60 p-10 soft-shadow"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary mb-8">
            <ArrowLeft className="h-3 w-3" /> Back to site
          </Link>
          <div className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-6">
            <Lock className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <p className="overline mb-3">Faith Family Finance</p>
          <h1 className="font-serif text-3xl text-primary leading-tight mb-2">Admin sign-in</h1>
          <p className="text-sm text-muted-foreground mb-8">Enter your password to view quote requests and client intakes.</p>
          <Label htmlFor="admin-pw" className="text-sm font-medium">Password</Label>
          <Input
            id="admin-pw"
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="h-12 rounded-xl mt-2"
            autoFocus
            required
          />
          <Button
            type="submit"
            data-testid="admin-submit"
            className="mt-6 w-full h-12 rounded-full bg-primary hover:bg-primary/90"
          >
            Sign in
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-card sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to site
            </Link>
            <div className="h-6 w-px bg-border" />
            <p className="font-serif text-xl text-primary">Admin</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadAll(password)}
              disabled={loading}
              className="rounded-full bg-transparent"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`h-3 w-3 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout">
              Sign out
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <nav className="flex gap-1">
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} count={leads.length} testid="admin-tab-leads">
              Quote requests
            </TabBtn>
            <TabBtn active={tab === "intakes"} onClick={() => setTab("intakes")} count={intakes.length} testid="admin-tab-intakes">
              Client intakes
            </TabBtn>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
        {tab === "leads" ? (
          <LeadList leads={leads} onToggleContacted={toggleContacted} />
        ) : (
          <IntakeList intakes={intakes} />
        )}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, count, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative px-5 py-3 text-sm font-medium transition-colors ${
        active ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {children}
        <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
          {count}
        </span>
      </span>
      {active && <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-accent rounded-full" />}
    </button>
  );
}

function LeadList({ leads, onToggleContacted }) {
  if (leads.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-border/70 bg-card/50 p-16 text-center">
        <p className="font-serif text-2xl text-primary mb-2">No quote requests yet</p>
        <p className="text-sm text-muted-foreground">Submissions to the homepage form will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="admin-leads-list">
      {leads.map((lead) => (
        <article
          key={lead.id}
          data-testid={`admin-lead-${lead.id}`}
          className={`rounded-3xl border border-border/60 bg-card p-6 lg:p-8 hover-lift transition-opacity ${
            lead.contacted ? "opacity-60" : ""
          }`}
        >
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {new Date(lead.created_at).toLocaleString()}
              </p>
              <h3 className={`font-serif text-2xl text-primary mt-1 ${lead.contacted ? "line-through decoration-accent/60" : ""}`}>
                {lead.name}
              </h3>
              <div className="mt-3 flex flex-col gap-1.5 text-sm">
                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 text-foreground hover:text-accent">
                  <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {lead.email}
                </a>
                <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 text-foreground hover:text-accent">
                  <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {lead.phone}
                </a>
              </div>

              <label
                className="mt-5 inline-flex items-center gap-2.5 cursor-pointer group select-none"
                data-testid={`admin-lead-contacted-${lead.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!lead.contacted}
                  onChange={(e) => onToggleContacted(lead.id, e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-accent cursor-pointer"
                />
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-foreground group-hover:text-accent">
                  Contacted
                </span>
                {lead.contacted && lead.contacted_at ? (
                  <span className="text-[10px] text-muted-foreground ml-1">
                    {new Date(lead.contacted_at).toLocaleDateString()}
                  </span>
                ) : null}
              </label>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <Cell label="Age" value={lead.age} />
              <Cell label="Tobacco" value={lead.tobacco_use} />
              <Cell label="Coverage" value={lead.coverage_amount} />
              <Cell label="Plan" value={lead.plan_interest || "—"} />
              {lead.message ? (
                <div className="col-span-2 lg:col-span-4 mt-1">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Message</p>
                  <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{lead.message}</p>
                </div>
              ) : null}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function IntakeList({ intakes }) {
  const [openId, setOpenId] = useState(null);

  if (intakes.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-border/70 bg-card/50 p-16 text-center">
        <p className="font-serif text-2xl text-primary mb-2">No intakes yet</p>
        <p className="text-sm text-muted-foreground">Client intake submissions will appear here. The full data + ID file is also emailed to Javier.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-intakes-list">
      {intakes.map((intake) => {
        const open = openId === intake.id;
        const d = intake.data || {};
        return (
          <article
            key={intake.id}
            data-testid={`admin-intake-${intake.id}`}
            className="rounded-3xl border border-border/60 bg-card p-6 lg:p-8"
          >
            <button
              onClick={() => setOpenId(open ? null : intake.id)}
              className="w-full text-left"
            >
              <div className="grid lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-5">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    {new Date(intake.created_at).toLocaleString()}
                  </p>
                  <h3 className="font-serif text-2xl text-primary mt-1 flex items-center gap-2">
                    <User className="h-4 w-4 text-accent" strokeWidth={1.5} />
                    {intake.client_name}
                  </h3>
                  <div className="mt-3 flex flex-col gap-1.5 text-sm">
                    <a href={`mailto:${intake.client_email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 text-foreground hover:text-accent">
                      <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {intake.client_email}
                    </a>
                    <a href={`tel:${intake.client_phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 text-foreground hover:text-accent">
                      <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {intake.client_phone}
                    </a>
                  </div>
                </div>
                <div className="lg:col-span-6 grid grid-cols-2 gap-4 text-sm">
                  <Cell label="Referring agent" value={intake.agent_name || "—"} />
                  <Cell label="DOB" value={d.date_of_birth || "—"} />
                  <Cell label="Marital" value={d.marital_status || "—"} />
                  <Cell label="US citizen" value={d.us_citizen || "—"} />
                  {intake.id_file_name ? (
                    <div className="col-span-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <FileText className="h-3.5 w-3.5 text-accent" strokeWidth={1.5} />
                      ID file: {intake.id_file_name} (sent via email)
                    </div>
                  ) : null}
                </div>
                <div className="lg:col-span-1 flex justify-end">
                  {open ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                </div>
              </div>
            </button>

            {open ? (
              <div className="mt-8 pt-8 border-t border-border/60 space-y-8" data-testid={`admin-intake-detail-${intake.id}`}>
                <p className="text-xs text-muted-foreground italic">
                  SSN and bank fields are partially masked here for security. The full unmasked record + ID file were emailed to javier@faithfamilyfinance.us at submission time.
                </p>
                {INTAKE_GROUPS.map((group) => {
                  const rows = group.keys
                    .map(([label, key]) => [label, d[key]])
                    .filter(([, v]) => v !== undefined && v !== null && v !== "");
                  if (rows.length === 0) return null;
                  return (
                    <div key={group.title}>
                      <p className="overline mb-3">{group.title}</p>
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                        {rows.map(([label, value]) => (
                          <div key={label} className="flex flex-col">
                            <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
                            <span className="text-sm text-foreground font-medium break-words mt-0.5 whitespace-pre-wrap">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</p>
      <p className="text-foreground font-medium break-words">{value}</p>
    </div>
  );
}
